import { useRef, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import useGetVehicles from '../../API/hooks/vehicles/useGetVehicles';
import useGetVehiclesLocations from '../../API/hooks/maps/useGetVehiclesLocations';
import { useDeleteVehicles } from '../../API/hooks/vehicles/useDeleteVehicle';
import { useGetVehicleLocationsByIds } from '../../API/hooks/maps/useGetVehicleLocations';
import { FilterConfig } from '../../components/common/DropdownFilter/types';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import Modal from '../../components/common/Modal/Modal';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import S from './VehiclesPageStyles';
import VehiclesScore from '../../components/VehiclesScoreWidget/VehiclesScoreWidget';
import ItemsList, { ItemsListRef } from '../../components/common/ItemsList/ItemsList';
import MapWidget from '../../components/common/Map/Map';
import VehiclesItem from './VehiclesItem/VehiclesItem';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import { AddVehicle } from './Modals';
import useGetRole from '../../API/hooks/useGetRole';
import { getLocalStorageKey } from '../../utils/storageUtils';
import ScorePeriodSelector from '../../components/common/Toolbar/ScorePeriodSelector';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import { getStartOfMonth } from '../../utils/dateUtils';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetVehiclesReport from '../../API/hooks/reports/useGetVehiclesReport';
import useOngoingTripMapIds from '../../API/hooks/trips/useOngoingTripMapIds';
import useGetCurrentPage from '../../API/hooks/useGetCurrentPage';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import { ThemeType } from '../../theme/themeType';
import { vehiclesPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import { useEntityScores } from '../../API/hooks/useEntityScores';
import { useVehicleAlertsCounts } from '../../API/hooks/vehicles/useVehicleAlertsCounts';

const PERIOD_TO_STATS_LABEL: Record<string, string> = {
  daily: 'Today’s statistics',
  weekly: 'This week’s statistics',
  monthly: 'This month’s statistics',
  all: 'All-time statistics',
};

const filterOptions: FilterConfig[] = [
  {
    type: 'category',
    display: 'radio',
    options: [
      { null: 'All' },
      { car: 'Car' },
      { minibus: 'Minibus' },
      { truck: 'Truck' },
    ],
  },
  {
    type: 'driverId',
    display: 'radio',
    options: [
      { null: 'All' },
      { assigned: 'Assigned' },
      { unassigned: 'Unassigned' },
    ],
  },
  {
    type: 'status',
    display: 'radio',
    options: [
      { null: 'All' },
      { parked: 'Parked' },
      { running: 'Running' },
      { idling: 'Idling' },
      { notResponding: 'Not resp.' },
    ],
  },
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      { driverId_DESC: 'Driver assigned: first' },
      { driverId_ASC: 'Driver assigned: last' },
      { distanceInKmMonthly_ASC: 'Monthly distance: asc' },
      { distanceInKmMonthly_DESC: 'Monthly distance: desc' },
      { licensePlateNumber_ASC: 'License plate number: asc' },
      { licensePlateNumber_DESC: 'License plate number: desc' },
      { score_ASC: 'Score: asc' },
      { score_DESC: 'Score: desc' },
      { alertCountMonthly_ASC: 'Monthly alerts: asc' },
      { alertCountMonthly_DESC: 'Monthly alerts: desc' },
    ],
  },
];

function VehiclesPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const fleetId = useGetFleetId();
  const { isFleetViewer } = useGetRole();
  const { data: fleetData } = useGetFleet(fleetId);
  const [addVehicleModalOpen, toggleAddVehicleModal] = useState(false);
  const [groupFilters, setGroupFilters] = useGroupFilters();
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const itemsListRef = useRef<ItemsListRef>(null);
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('vehicles-page-filters'), vehiclesPageFilters);
  const { startDate, endDate, ...vehicleFilters } = filters || vehiclesPageFilters;
  const { data: allVehiclesData } = useGetVehicles(fleetId, '?limit=1000&skip=0');
  const vehicleIds = (allVehiclesData?.results ?? []).map((v) => v._id);
  const { scores: computedVehicleScores, isLoading: isScoresLoading } = useEntityScores(fleetId, vehicleIds, 'vehicle');
  const { alertCounts: computedVehicleAlertCounts } = useVehicleAlertsCounts(fleetId, vehicleIds);

  const period = filters?.period?.[0];
  const isAllTime = period === 'custom' && filters?.startDate?.[0] === new Date(0).toISOString();
  const normalizedPeriod = isAllTime ? 'all' : period;
  const scoreField = ({ daily: 'scoreDaily', weekly: 'scoreWeekly', monthly: 'scoreMonthly' } as Record<string, string>)[period ?? ''] ?? 'score';
  const statsLabel = PERIOD_TO_STATS_LABEL[normalizedPeriod] ?? 'All-time statistics';

  const nullScores = {
    score: null, scoreDaily: null, scoreWeekly: null, scoreMonthly: null,
  };
  const vehiclesWithComputedScores = (allVehiclesData?.results ?? []).map((v) => {
    const cs = computedVehicleScores.get(v._id);
    return { ...v, ...nullScores, ...(cs ?? {}) } as typeof v;
  });

  const averageScore = useMemo<number | null>(() => {
    if (isScoresLoading || vehiclesWithComputedScores.length === 0) return null;
    const valid = vehiclesWithComputedScores
      .map((v) => v[scoreField as keyof typeof v] as number | null)
      .filter((s): s is number => s != null && s > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
  }, [vehiclesWithComputedScores, scoreField, isScoresLoading]);
  const { mutate: mutateDelete } = useDeleteVehicles(
    () => {
      toast.success('Vehicle(s) deleted successfully');
      itemsListRef.current?.toggleSelection(false);
    },
    () => toast.error('One ore more vehicle(s) failed to delete'),
  );

  const handleDeleteVehicles = (ids: string[]): void => mutateDelete({
    fleetId,
    ids,
  });

  const handleDeleteItems = (): void => {
    handleDeleteVehicles(selectedItems);
    toggleDeleteModalOpen(false);
  };

  const BUTTONS = [
    {
      title: 'Add new vehicle',
      iconKey: IconKeys.PLUS,
      action: () => toggleAddVehicleModal(true),
      hide: isFleetViewer,
    },
  ];

  const LIST_BUTTONS = [
    {
      title: `Delete${selectedItems.length > 0 ? ` ${selectedItems.length} vehicle(s)` : ''}`,
      action: () => toggleDeleteModalOpen(true),
      hide: isFleetViewer,
      height: '25px',
      background: theme.colors.red,
      hoverBackground: theme.colors.red,
      disabled: selectedItems.length < 1,
    },
  ];

  const reportName = `${fleetData?.name || 'fleet_na'}_vehicles`;
  const { ongoingVehicleIds, ongoingTripSpeedDisplayByVehicleId } = useOngoingTripMapIds(fleetId);

  return (
    <>
      <S.Wrapper>
        <Toolbar title="Vehicles" buttons={BUTTONS} />
        <div className="sub-header">
          <ScorePeriodSelector filters={filters} setFilters={setFilters} />
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
        <div className="widgets-container">
          <MapWidget
            fleetId={fleetId}
            entityType="vehicle"
            getLocationsQuery={useGetVehiclesLocations}
            getLocationDetailsQuery={useGetVehicleLocationsByIds}
            groupFilters={groupFilters || {}}
            fleetOngoingVehicleIds={ongoingVehicleIds}
            ongoingTripSpeedDisplayByVehicleId={ongoingTripSpeedDisplayByVehicleId}
            showSpeedInVehicleInfo
            mapWrapperWidth="80%"
          />
          <VehiclesScore
            fleetId={fleetId}
            groupFilters={groupFilters || {}}
            reportName={reportName}
            averageScore={averageScore}
            period={normalizedPeriod}
            allVehicles={vehiclesWithComputedScores}
          />
        </div>
        <ItemsList
          ref={itemsListRef}
          entity="vehicles"
          title={statsLabel}
          fleetId={fleetId}
          filtersConfig={filterOptions}
          filters={vehicleFilters}
          defaultFilters={vehiclesPageFilters}
          setFilters={setFilters}
          groupFilters={groupFilters || {}}
          getQuery={useGetVehicles}
          hasSearch
          hasCreateReport
          reportEndpoint={useGetVehiclesReport}
          reportName={reportName}
          buttons={LIST_BUTTONS}
          onSelect={setSelectedItems}
          curentPage={useGetCurrentPage()}
          scoreOverrides={computedVehicleScores}
          alertCountOverrides={computedVehicleAlertCounts}
        >
          <VehiclesItem />
        </ItemsList>
      </S.Wrapper>
      {addVehicleModalOpen && (
        <Modal
          title="Add new vehicle"
          state={addVehicleModalOpen}
          toggleState={toggleAddVehicleModal}
        >
          <AddVehicle fleetId={fleetId} toggleModal={toggleAddVehicleModal} />
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete these vehicle(s)"
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={handleDeleteItems}
          />
        </Modal>
      )}
    </>
  );
}

export default VehiclesPage;
